// DEPRECATED (2026-07-19): pwa/src/screens/ is scheduled for deletion in STEP-M8-V4-LEGACY-SCREENS.
// Active screens are in pwa/src/v4/screens/ — use those instead.
import { useState, useEffect, useCallback, useRef } from "react";
import { fetchEntities, fetchEntity, fetchEntitySchema } from "../api/client.js";
import type { EntitySchemaType } from "../api/client.js";
import type { NavigationIntent } from "../api/types.js";

const ENTITY_TYPES = [
  { key: "steps", label: "Steps" },
  { key: "milestones", label: "Milestones" },
  { key: "vlrs", label: "VLRs" },
  { key: "decisions", label: "Decisions" },
  { key: "spikes", label: "Spikes" },
  { key: "milestone-reviews", label: "Reviews" },
] as const;

type EntityTypeKey = (typeof ENTITY_TYPES)[number]["key"];

function typeFromHash(): EntityTypeKey | null {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash.startsWith("entities/")) return null;
  const sub = hash.slice("entities/".length);
  const normalized = sub === "vlr" ? "vlrs" : sub;
  return ENTITY_TYPES.some((t) => t.key === normalized) ? (normalized as EntityTypeKey) : null;
}

const STATUS_COLORS: Record<string, string> = {
  complete: "ok",
  completed: "ok",
  integrated: "ok",
  merged: "ok",
  active: "accent",
  in_progress: "accent",
  running: "accent",
  pending_review: "warn",
  proposed: "warn",
  pending: "warn",
  triaged: "warn",
  failed: "danger",
  error: "danger",
  rejected: "danger",
  cancelled: "danger",
};

function statusChip(status: string): string {
  // STATUS_COLORS is lowercase-keyed; statuses arrive mixed-case (e.g. "Proposed").
  const cls = STATUS_COLORS[status?.toLowerCase()] ?? "";
  return cls ? `chip ${cls}` : "chip";
}

// ── Inline markdown renderer ───────────────────────────────────

function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlineFormat(s: string): string {
  let result = s.replace(/`([^`]+)`/g, (_m, c: string) => `<code>${escHtml(c)}</code>`);
  result = result.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  result = result.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  result = result.replace(
    /\b(STEP-\d+|VLR-\d+|DEC-\d+|SPIKE-\w+|M-[A-Z0-9]+)\b/g,
    '<span class="chip accent" style="cursor:pointer;font-size:10px" data-entity-ref="$1">$1</span>',
  );
  return result;
}

function renderMarkdown(raw: string): string {
  if (!raw) return "";
  const lines = raw.split("\n");
  const out: string[] = [];
  let inCode = false;
  let codeLines: string[] = [];
  let inList = false;

  for (const line of lines) {
    if (line.startsWith("```")) {
      if (inCode) {
        out.push(`<pre><code>${codeLines.join("\n")}</code></pre>`);
        codeLines = [];
        inCode = false;
      } else {
        if (inList) {
          out.push("</ul>");
          inList = false;
        }
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeLines.push(escHtml(line));
      continue;
    }
    if (line.startsWith("### ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h4>${escHtml(line.slice(4))}</h4>`);
    } else if (line.startsWith("## ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h3>${escHtml(line.slice(3))}</h3>`);
    } else if (line.startsWith("# ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h2>${escHtml(line.slice(2))}</h2>`);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inlineFormat(line.slice(2))}</li>`);
    } else if (line.trim() === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push("<br/>");
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p>${inlineFormat(line)}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("");
}

// ── Cross-reference navigation ─────────────────────────────────

function entityTypeFromId(id: string): EntityTypeKey | null {
  if (/^STEP-\d+$/.test(id)) return "steps";
  if (/^VLR-\d+$/.test(id)) return "vlrs";
  if (/^DEC-\d+$/.test(id)) return "decisions";
  if (/^SPIKE-/.test(id)) return "spikes";
  if (/^M-[A-Z0-9]+$/.test(id)) return "milestones";
  return null;
}

// ── EntityCard ─────────────────────────────────────────────────

function EntityCard({
  entity,
  onTap,
}: {
  entity: Record<string, unknown>;
  onTap: () => void;
}) {
  const id = entity.id as string;
  const title = entity.title as string | null;
  const status = entity.status as string | null;
  const domain = entity.domain as string | null;
  const tags = entity.tags as string[] | null;
  const severity = entity.severity as string | null;

  return (
    <button
      className="card"
      style={{
        display: "block",
        width: "calc(100% - 40px)",
        textAlign: "left",
        cursor: "pointer",
      }}
      onClick={onTap}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span className="mono" style={{ color: "var(--accent)", fontSize: 10, fontWeight: 700 }}>
          {id}
        </span>
        <span style={{ flex: 1 }} />
        {status && <span className={statusChip(status)}>{status}</span>}
        {severity && severity !== status && (
          <span className={statusChip(severity)}>{severity}</span>
        )}
      </div>
      {title && (
        <div style={{ fontSize: 13, color: "var(--text)", marginBottom: 4, lineHeight: 1.4 }}>
          {title}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {domain && (
          <span className="chip" style={{ fontSize: 10 }}>{domain}</span>
        )}
        {(tags ?? []).slice(0, 3).map((t) => (
          <span key={t} className="chip" style={{ fontSize: 10 }}>{t}</span>
        ))}
      </div>
    </button>
  );
}

// ── EntityDetailSheet ──────────────────────────────────────────

function EntityDetailSheet({
  entity,
  loading,
  onClose,
  onNavigate,
}: {
  entity: Record<string, unknown> | null;
  loading: boolean;
  onClose: () => void;
  onNavigate: (type: EntityTypeKey, id: string) => void;
}) {
  const isOpen = entity !== null || loading;
  const description = entity?.description as string | null;
  const markdownHtml = description ? renderMarkdown(description) : null;
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bodyRef.current) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const ref = target.closest("[data-entity-ref]");
      if (!ref) return;
      const refId = (ref as HTMLElement).dataset.entityRef;
      if (!refId) return;
      const type = entityTypeFromId(refId);
      if (type) {
        e.stopPropagation();
        onNavigate(type, refId);
      }
    }
    const el = bodyRef.current;
    el.addEventListener("click", handleClick);
    return () => el.removeEventListener("click", handleClick);
  }, [onNavigate, entity]);

  const id = entity?.id as string | null;
  const title = entity?.title as string | null;
  const status = entity?.status as string | null;
  const phase = entity?.phase as string | null;
  const domain = entity?.domain as string | null;
  const tags = entity?.tags as string[] | null;
  const validFrom = entity?.valid_from as string | null;
  const crossRefs = entity?.cross_refs as Record<string, unknown>[] | null;
  const childSteps = entity?.child_steps as Record<string, unknown>[] | null;
  const parentMilestone = entity?.parent_milestone as Record<string, unknown> | null;

  return (
    <>
      <div
        className={`sheet-backdrop${isOpen ? " open" : ""}`}
        style={{ position: "fixed" }}
        onClick={onClose}
      />
      <div
        className={`sheet${isOpen ? " open" : ""}`}
        style={{ position: "fixed" }}
      >
        <div className="sheet-handle" />
        {loading && !entity && (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "var(--text-dim)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
            }}
          >
            Loading...
          </div>
        )}
        {entity && (
          <>
            <div className="sheet-head">
              <div className="eyebrow">{id}</div>
              <h3 style={{ fontSize: 15, lineHeight: 1.35 }}>{title}</h3>
              {status && <span className={statusChip(status)}>{status}</span>}
            </div>
            <div className="sheet-body" ref={bodyRef}>
              <div className="sheet-section">
                <h4>Metadata</h4>
                {phase && (
                  <div className="sheet-row">
                    <span className="k">Phase</span>
                    <span className="v mono" style={{ fontSize: 11 }}>{phase}</span>
                  </div>
                )}
                {domain && (
                  <div className="sheet-row">
                    <span className="k">Domain</span>
                    <span className="v">{domain}</span>
                  </div>
                )}
                {validFrom && (
                  <div className="sheet-row">
                    <span className="k">Created</span>
                    <span className="v mono" style={{ fontSize: 11 }}>
                      {new Date(validFrom).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {(tags ?? []).length > 0 && (
                  <div className="sheet-row">
                    <span className="k">Tags</span>
                    <span className="v" style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {(tags ?? []).map((t) => (
                        <span key={t} className="chip" style={{ fontSize: 10 }}>{t}</span>
                      ))}
                    </span>
                  </div>
                )}
              </div>

              {parentMilestone && (
                <div className="sheet-section">
                  <h4>Milestone</h4>
                  <button
                    className="card"
                    style={{ display: "block", width: "100%", textAlign: "left", cursor: "pointer" }}
                    onClick={() => onNavigate("milestones", parentMilestone.id as string)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span className="mono" style={{ color: "var(--accent)", fontSize: 10 }}>
                        {parentMilestone.id as string}
                      </span>
                      <span style={{ flex: 1, fontSize: 12 }}>{parentMilestone.title as string}</span>
                      <span className={statusChip(parentMilestone.status as string)}>
                        {parentMilestone.status as string}
                      </span>
                    </div>
                  </button>
                </div>
              )}

              {markdownHtml && (
                <div className="sheet-section">
                  <h4>Description</h4>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      lineHeight: 1.6,
                    }}
                    dangerouslySetInnerHTML={{ __html: markdownHtml }}
                  />
                </div>
              )}

              {(childSteps ?? []).length > 0 && (
                <div className="sheet-section">
                  <h4>Steps ({(childSteps ?? []).length})</h4>
                  {(childSteps ?? []).map((s) => (
                    <button
                      key={s.id as string}
                      className="card"
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                        marginBottom: 4,
                      }}
                      onClick={() => onNavigate("steps", s.id as string)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="mono" style={{ color: "var(--accent)", fontSize: 10 }}>
                          {s.id as string}
                        </span>
                        <span style={{ flex: 1, fontSize: 12 }}>{s.title as string}</span>
                        <span className={statusChip(s.status as string)}>
                          {s.status as string}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {(crossRefs ?? []).length > 0 && (
                <div className="sheet-section">
                  <h4>Cross-references ({(crossRefs ?? []).length})</h4>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(crossRefs ?? []).map((ref) => {
                      const refId = ref.id as string;
                      const refType = entityTypeFromId(refId);
                      return (
                        <button
                          key={refId}
                          className="chip accent"
                          style={{ cursor: "pointer", border: "none", background: "none" }}
                          onClick={() => { if (refType) onNavigate(refType, refId); }}
                        >
                          {refId}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── WikiView ───────────────────────────────────────────────────
// Exported so the v4 Entities tab (PWA V2 slice 4) reuses it as its
// "Type Reference" view-mode without duplication.
export function WikiView() {
  const [schema, setSchema] = useState<EntitySchemaType[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchEntitySchema()
      .then((data) => {
        setSchema(data.types);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = (schema ?? []).filter(
    (t) =>
      !search ||
      (t.label ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (t.type ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (t.description ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <div style={{ padding: "0 20px 12px" }}>
        <input
          type="text"
          placeholder="Filter types..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid var(--border-soft)",
            background: "var(--bg-elev)",
            color: "var(--text)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            outline: "none",
          }}
        />
      </div>

      <div className="section-head">
        <h2>Entity Types</h2>
        {!loading && <span className="count">{filtered.length}</span>}
      </div>

      {loading && (
        <div
          className="skeleton"
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
          }}
        >
          Loading...
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="quiet-state">
          <div className="glyph">◉</div>
          <h3>No types found</h3>
          <p>{search ? "Try adjusting your filter." : "Schema unavailable."}</p>
        </div>
      )}

      {filtered.map((t) => (
        <div key={t.type} className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span className="mono" style={{ color: "var(--accent)", fontSize: 10, fontWeight: 700 }}>
              {t.idPattern}
            </span>
            <span style={{ flex: 1 }} />
            <span className="chip" style={{ fontSize: 10 }}>{t.format}</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>
            {t.label}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
            {t.description}
          </div>

          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--text-dim)",
                marginBottom: 4,
              }}
            >
              Key Fields
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {t.keyFields.map((f) => (
                <span key={f} className="chip" style={{ fontSize: 10, fontFamily: "var(--font-mono)" }}>
                  {f}
                </span>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--text-dim)",
                marginBottom: 4,
              }}
            >
              Statuses
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {t.validStatuses.map((s) => (
                <span key={s} className={statusChip(s.toLowerCase())} style={{ fontSize: 10 }}>
                  {s}
                </span>
              ))}
            </div>
          </div>

          {t.relatedTypes.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--text-dim)",
                  marginBottom: 4,
                }}
              >
                Related
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {t.relatedTypes.map((r) => (
                  <span key={r} className="chip accent" style={{ fontSize: 10 }}>
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div
            style={{
              fontSize: 10,
              color: "var(--text-dim)",
              fontFamily: "var(--font-mono)",
              marginTop: 6,
            }}
          >
            {t.directory}
          </div>
        </div>
      ))}

      <div style={{ height: 80 }} />
    </>
  );
}

// ── EntitiesScreen ─────────────────────────────────────────────

interface EntitiesScreenProps {
  onNavigate?: (intent: string | NavigationIntent) => void;
}

export function EntitiesScreen({ onNavigate: _onExternalNavigate }: EntitiesScreenProps = {}) {
  const [view, setView] = useState<"browse" | "wiki">("browse");
  const [activeType, setActiveType] = useState<EntityTypeKey>(() => typeFromHash() ?? "steps");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [entities, setEntities] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    function onHashChange() {
      const t = typeFromHash();
      if (t) setActiveType(t);
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const loadEntities = useCallback(
    async (
      type: EntityTypeKey,
      opts: { status?: string; search?: string; cursor?: string } = {},
      append = false,
    ) => {
      try {
        const res = await fetchEntities(type, {
          status: opts.status || undefined,
          search: opts.search || undefined,
          cursor: opts.cursor,
        });
        const items = (res.entities ?? []) as Record<string, unknown>[];
        setEntities(append ? (prev) => [...prev, ...items] : items);
        setTotal(res.total ?? 0);
        setNextCursor(res.next_cursor);
      } catch {
        // silently fail
      }
    },
    [],
  );

  useEffect(() => {
    setLoading(true);
    setEntities([]);
    setNextCursor(null);
    void loadEntities(activeType, { status: statusFilter, search }).finally(() =>
      setLoading(false),
    );
  }, [activeType, search, statusFilter, loadEntities]);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    await loadEntities(
      activeType,
      { status: statusFilter, search, cursor: nextCursor },
      true,
    );
    setLoadingMore(false);
  }, [nextCursor, loadingMore, activeType, statusFilter, search, loadEntities]);

  const openDetail = useCallback(async (type: EntityTypeKey, id: string) => {
    setDetail(null);
    setDetailLoading(true);
    try {
      const entity = await fetchEntity(type, id);
      setDetail(entity);
    } catch {
      // silently fail
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setDetail(null);
  }, []);

  const handleNavigate = useCallback(
    (type: EntityTypeKey, id: string) => {
      void openDetail(type, id);
    },
    [openDetail],
  );

  return (
    <>
      {/* View mode toggle */}
      <div style={{ display: "flex", gap: 6, padding: "12px 20px 8px" }}>
        {(["browse", "wiki"] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              padding: "7px 14px",
              minHeight: 44,
              borderRadius: 20,
              border: "1px solid",
              borderColor: view === v ? "var(--accent)" : "var(--border-soft)",
              background: view === v ? "var(--accent-soft)" : "transparent",
              color: view === v ? "var(--accent)" : "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: view === v ? 700 : 400,
              cursor: "pointer",
            }}
          >
            {v === "browse" ? "Browse Records" : "Type Reference"}
          </button>
        ))}
      </div>

      {view === "wiki" ? (
        <WikiView />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              overflowX: "auto",
              gap: 6,
              padding: "0 20px 12px",
              scrollbarWidth: "none",
            }}
          >
            {ENTITY_TYPES.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => {
                  setActiveType(key);
                  setSearchInput("");
                  setSearch("");
                  setStatusFilter("");
                }}
                style={{
                  flexShrink: 0,
                  padding: "5px 12px",
                  minHeight: 44,
                  borderRadius: 20,
                  border: "1px solid",
                  borderColor: activeType === key ? "var(--accent)" : "var(--border-soft)",
                  background: activeType === key ? "var(--accent-soft)" : "transparent",
                  color: activeType === key ? "var(--accent)" : "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  fontWeight: activeType === key ? 700 : 400,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ padding: "0 20px 12px", display: "flex", gap: 8 }}>
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                flex: 1,
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid var(--border-soft)",
                background: "var(--bg-elev)",
                color: "var(--text)",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                outline: "none",
              }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid var(--border-soft)",
                background: "var(--bg-elev)",
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                outline: "none",
              }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="complete">Complete</option>
              <option value="in_progress">In progress</option>
              <option value="pending">Pending</option>
              <option value="proposed">Proposed</option>
              <option value="triaged">Triaged</option>
              <option value="integrated">Integrated</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="section-head">
            <h2>{ENTITY_TYPES.find((t) => t.key === activeType)?.label}</h2>
            {!loading && <span className="count">{total}</span>}
          </div>

          {loading && (
            <div
              className="skeleton"
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "var(--text-dim)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              Loading...
            </div>
          )}

          {!loading && entities.length === 0 && (
            <div className="quiet-state">
              <div className="glyph">◉</div>
              <h3>No {ENTITY_TYPES.find((t) => t.key === activeType)?.label}</h3>
              <p>{search || statusFilter ? "Try adjusting your filters." : "Nothing here yet."}</p>
            </div>
          )}

          {entities.map((e) => (
            <EntityCard
              key={e.id as string}
              entity={e}
              onTap={() => void openDetail(activeType, e.id as string)}
            />
          ))}

          {nextCursor && (
            <div style={{ padding: "12px 20px" }}>
              <button
                onClick={() => void handleLoadMore()}
                disabled={loadingMore}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: 8,
                  border: "1px solid var(--border-soft)",
                  background: "transparent",
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  cursor: loadingMore ? "default" : "pointer",
                }}
              >
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          )}

          <div style={{ height: 80 }} />

          <EntityDetailSheet
            entity={detail}
            loading={detailLoading}
            onClose={closeDetail}
            onNavigate={handleNavigate}
          />
        </>
      )}
    </>
  );
}
